import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { calculateOptimalPace, calculateMentalResilienceWithHistory } from '../../services/funnelService';

const Container = styled.div`
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  gap: ${theme.spacing.lg};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0;
  line-height: ${theme.typography.lineHeights.relaxed};
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const InfoBox = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  border: 2px solid ${theme.colors.neutral}10;
  text-align: center;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary}20;
  }
`;

const InfoLabel = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const InfoValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const InfoDescription = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin: 0;
  line-height: ${theme.typography.lineHeights.normal};
`;

const ResilienceValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ResilienceSubtext = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.normal};
  color: ${theme.colors.textLight};
`;

const ActionsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const ActionBox = styled.button`
  background: white;
  border: 2px solid ${theme.colors.primary}20;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  text-align: left;
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary}40;
    background: ${theme.colors.primary}05;
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
`;

const ActionTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const ActionDescription = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0;
  line-height: ${theme.typography.lineHeights.relaxed};
`;

const ActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary}10;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  background: ${theme.colors.neutral}05;
  border-radius: ${theme.borderRadius.md};
  border: 2px dashed ${theme.colors.neutral}20;
`;

const DashboardScreen = ({ data, userProgress = {}, onNext }) => {
  // Calculate current Optimal Pace from most recent journal entry
  const calculateCurrentOptimalPace = () => {
    if (!userProgress.journalEntries || userProgress.journalEntries.length === 0) {
      return null;
    }
    
    const mostRecentEntry = userProgress.journalEntries
      .filter(entry => entry.emotionalStates && entry.hoursWorked)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    
    if (!mostRecentEntry) {
      return null;
    }
    
    try {
      const optimalPace = calculateOptimalPace(mostRecentEntry);
      // Return as whole number of hours
      const wholeHours = Math.round(optimalPace.optimalHours);
      return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    } catch (error) {
      console.error('Error calculating current optimal pace:', error);
      return null;
    }
  };

  // Calculate current Mental Resilience level
  const calculateCurrentMentalResilience = () => {
    if (!userProgress.journalEntries || userProgress.journalEntries.length === 0) {
      return null;
    }
    
    try {
      const resilience = calculateMentalResilienceWithHistory(userProgress.journalEntries);
      return resilience.score;
    } catch (error) {
      console.error('Error calculating mental resilience:', error);
      return null;
    }
  };

  // Calculate current Mental Reserves (sum of seven emotional factors)
  const calculateCurrentMentalReserves = () => {
    if (!userProgress.journalEntries || userProgress.journalEntries.length === 0) {
      return null;
    }
    
    const mostRecentEntry = userProgress.journalEntries
      .filter(entry => entry.emotionalStates)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    
    if (!mostRecentEntry || !mostRecentEntry.emotionalStates) {
      return null;
    }
    
    const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
    const scores = emotionalFactors.map(factor => mostRecentEntry.emotionalStates[factor] || 0);
    const total = scores.reduce((sum, score) => sum + score, 0);
    
    // Calculate percentage (total out of 70, then convert to percentage)
    const percentage = Math.round((total / 70) * 100);
    return percentage;
  };

  const currentOptimalPace = calculateCurrentOptimalPace();
  const currentMentalResilience = calculateCurrentMentalResilience();
  const currentMentalReserves = calculateCurrentMentalReserves();

  const handleActionClick = (actionType) => {
    // Handle different action types
    switch (actionType) {
      case 'check_in':
        // Navigate to journal entry screen
        if (onNext) {
          onNext(data.key);
        }
        break;
      case 'learn_superpowers':
        // Navigate to learning screen
        if (onNext) {
          onNext(data.key);
        }
        break;
      case 'edit_profile':
        // Navigate to membership-entry screen
        if (onNext) {
          onNext(data.key, { action: 'navigate_to_screen', screenKey: 'membership-entry' });
        }
        break;
      case 'mental_resilience_score':
        // Navigate to detailed mental resilience view
        if (onNext) {
          onNext(data.key);
        }
        break;
      case 'leaderboard':
        // Navigate to leaderboard
        if (onNext) {
          onNext(data.key);
        }
        break;
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const hasData = currentOptimalPace || currentMentalResilience !== null || currentMentalReserves !== null;

  return (
    <Container>
      <Header>
      </Header>

      {!hasData && (
        <NoDataMessage>
          Complete your first check-in to see your personalized dashboard metrics.
        </NoDataMessage>
      )}

      {hasData && (
        <>
          <InfoSection>
            <InfoBox>
              <InfoLabel>Optimal Pace for Today</InfoLabel>
              <InfoValue>
                {currentOptimalPace || 'Not calculated'}
              </InfoValue>
              <InfoDescription>
                Working more than your Optimal Pace today will reduce your Mental Resilience and your Optimal Pace tomorrow. Your Optimal Pace changes daily.
              </InfoDescription>
            </InfoBox>

            <InfoBox>
              <InfoLabel>Mental Resilience</InfoLabel>
              <ResilienceValue>
                {currentMentalResilience !== null ? (
                  <>
                    {currentMentalResilience} <ResilienceSubtext>out of 100</ResilienceSubtext>
                  </>
                ) : (
                  'Not calculated'
                )}
              </ResilienceValue>
              <InfoDescription>
                How strongly your Mental Reserves persist when pressure goes up. Mental Resilience changes over weeks and months.
              </InfoDescription>
            </InfoBox>

            <InfoBox>
              <InfoLabel>Mental Reserves</InfoLabel>
              <InfoValue>
                {currentMentalReserves !== null ? `${currentMentalReserves}%` : 'Not calculated'}
              </InfoValue>
              <InfoDescription>
                Your Mental Reserves support your performance.
              </InfoDescription>
            </InfoBox>
          </InfoSection>

          <ActionsSection>
            <ActionBox onClick={() => handleActionClick('check_in')}>
              <ActionIcon>📝</ActionIcon>
              <ActionTitle>Check in to see my Optimal Pace for today</ActionTitle>
              <ActionDescription>
                Record your current emotional state and work hours to get your personalized optimal pace recommendation.
              </ActionDescription>
            </ActionBox>

            <ActionBox onClick={() => handleActionClick('learn_superpowers')}>
              <ActionIcon>🧠</ActionIcon>
              <ActionTitle>Learn the 3 Superpowers of Superhuman Mental Athletes</ActionTitle>
              <ActionDescription>
                Master the three core mental abilities that separate superhuman performers from everyone else.
              </ActionDescription>
            </ActionBox>

            <ActionBox onClick={() => handleActionClick('edit_profile')}>
              <ActionIcon>👤</ActionIcon>
              <ActionTitle>Edit my community profile</ActionTitle>
              <ActionDescription>
                Update your personal information and preferences for the community.
              </ActionDescription>
            </ActionBox>

            <ActionBox onClick={() => handleActionClick('mental_resilience_score')}>
              <ActionIcon>📊</ActionIcon>
              <ActionTitle>See my high-definition Mental Resilience score</ActionTitle>
              <ActionDescription>
                View detailed analysis of your mental resilience with statistical confidence levels and recommendations.
              </ActionDescription>
            </ActionBox>

            <ActionBox onClick={() => handleActionClick('leaderboard')}>
              <ActionIcon>🏆</ActionIcon>
              <ActionTitle>See the community leaderboard</ActionTitle>
              <ActionDescription>
                Compare your mental performance with other community members and see how you rank.
              </ActionDescription>
            </ActionBox>
          </ActionsSection>
        </>
      )}
    </Container>
  );
};

export default DashboardScreen;
